<template>
  <div class="pc-panel" v-loading="loading">
    <div class="pc-toolbar">
      <div class="pc-path" aria-label="当前位置">
        <span class="pc-path__item" :class="{ 'is-dim': !activeBook }">课本册</span>
        <i class="el-icon-arrow-right pc-path__sep"></i>
        <span class="pc-path__item" :class="{ 'is-on': !!activeBook, 'is-dim': !activeBook }">
          {{ activeBook ? activeBook.Title : "选择课本册" }}
        </span>
        <i class="el-icon-arrow-right pc-path__sep"></i>
        <span class="pc-path__item" :class="{ 'is-on': !!activeUnit, 'is-dim': !activeUnit }">
          {{ activeUnit ? activeUnit.UnitName : "选择单元" }}
        </span>
      </div>
      <div class="pc-toolbar__actions">
        <el-button type="primary" size="mini" icon="el-icon-plus" @click="openBookDialog()">新增课本册</el-button>
        <el-button size="mini" plain icon="el-icon-refresh" @click="reloadAll">刷新</el-button>
      </div>
    </div>

    <div class="pc-shell">
      <!-- 左：课本册 -->
      <aside class="pc-pane pc-pane--books">
        <div class="pc-pane__head">
          <span class="pc-pane__title">课本册</span>
          <span class="pc-pane__count">{{ filteredTextbooks.length }}</span>
        </div>
        <div class="pc-pane__search">
          <el-input
            v-model="bookKeyword"
            size="mini"
            clearable
            prefix-icon="el-icon-search"
            placeholder="搜索课本册"
          />
        </div>
        <div v-if="filteredTextbooks.length" class="pc-list" role="listbox">
          <button
            v-for="b in filteredTextbooks"
            :key="b.Id"
            type="button"
            class="pc-row"
            :class="{ 'is-active': Number(b.Id) === Number(activeTextbookId) }"
            @click="onSelectBook(b.Id)"
          >
            <span class="pc-row__main">{{ b.Title }}</span>
            <span class="pc-row__aside">{{ b.UnitCount || 0 }} 单元</span>
          </button>
        </div>
        <div v-else class="pc-empty pc-empty--sm">
          <p>{{ textbooks.length ? "无匹配课本册" : "暂无课本册" }}</p>
          <el-button v-if="!textbooks.length" type="text" @click="openBookDialog()">去新增</el-button>
        </div>
        <div v-if="activeBook" class="pc-pane__foot">
          <el-button type="text" size="mini" @click="openBookDialog(activeBook)">编辑</el-button>
          <el-button type="text" size="mini" class="is-danger" @click="removeBook">删除</el-button>
        </div>
      </aside>

      <!-- 中：单元 -->
      <aside class="pc-pane pc-pane--units">
        <template v-if="activeTextbookId">
          <div class="pc-pane__head">
            <div class="pc-pane__head-left">
              <span class="pc-pane__title">单元</span>
              <span class="pc-pane__count">{{ filteredUnits.length }}</span>
            </div>
            <el-button type="text" size="mini" icon="el-icon-plus" @click="openUnitDialog()">新增</el-button>
          </div>
          <div class="pc-pane__search">
            <el-input
              v-model="unitKeyword"
              size="mini"
              clearable
              prefix-icon="el-icon-search"
              placeholder="搜索单元"
            />
          </div>
          <div v-if="filteredUnits.length" class="pc-list" role="listbox">
            <button
              v-for="u in filteredUnits"
              :key="u.Id"
              type="button"
              class="pc-row"
              :class="{ 'is-active': Number(u.Id) === Number(activeUnitId) }"
              @click="onSelectUnit(u.Id)"
            >
              <span class="pc-row__top">
                <span class="pc-row__main">{{ u.UnitName }}</span>
                <span class="pc-row__tags">
                  <span v-if="!isUnitAssigned(u)" class="pc-badge-warn">未安排</span>
                  <span v-if="u.PageNo" class="pc-row__page">P{{ u.PageNo }}</span>
                </span>
              </span>
              <span class="pc-row__meta">
                <span class="pc-chip">新 {{ lessonCountOf(u, 1) }}/20</span>
                <span class="pc-chip">练 {{ lessonCountOf(u, 2) }}/20</span>
              </span>
            </button>
          </div>
          <div v-else class="pc-empty pc-empty--sm">
            <p>{{ units.length ? "无匹配单元" : "暂无单元" }}</p>
            <el-button v-if="!units.length" type="text" @click="openUnitDialog()">去新增</el-button>
          </div>
          <div class="pc-pane__foot">
            <span class="pc-foot-hint" title="Excel 一表导入单元与课时">目录导入</span>
            <el-button type="text" size="mini" icon="el-icon-download" title="下载模板（含课时列）" @click="downloadTemplate">模板</el-button>
            <el-button type="text" size="mini" icon="el-icon-upload2" title="导入单元与课时到当前课本册" :loading="importing" @click="triggerImport">导入</el-button>
            <input ref="importFile" type="file" accept=".xlsx,.xls" class="pc-file" @change="onImportFile" />
          </div>
        </template>
        <div v-else class="pc-empty">
          <i class="el-icon-notebook-2"></i>
          <p>请先选择课本册</p>
        </div>
      </aside>

      <!-- 右：单元详情 -->
      <section class="pc-pane pc-pane--detail">
        <template v-if="activeUnit">
          <div class="pc-detail-head">
            <div class="pc-detail-head__text">
              <h3>{{ activeUnit.UnitName }}</h3>
              <p>
                <span v-if="activeUnit.PageNo">页码 P{{ activeUnit.PageNo }}</span>
                <span v-else>未填页码</span>
                <span class="pc-dot">·</span>
                <span>新授 {{ lessonCountOf(activeUnit, 1) }}/20</span>
                <span class="pc-dot">·</span>
                <span>练习 {{ lessonCountOf(activeUnit, 2) }}/20</span>
              </p>
            </div>
            <div class="pc-detail-head__ops">
              <el-button type="text" size="mini" @click="openUnitDialog(activeUnit)">编辑</el-button>
              <el-button type="text" size="mini" class="is-danger" @click="removeUnit()">删除</el-button>
            </div>
          </div>

          <div class="pc-assign">
            <label class="pc-assign__label">安排教师</label>
            <div class="pc-assign-body">
              <div ref="assignChipsRow" class="pc-assign-chips">
                <el-tag
                  v-for="id in visibleAssignIds"
                  :key="'a-' + id"
                  size="mini"
                  closable
                  disable-transitions
                  @close="removeAssignId(id)"
                >{{ teacherNameOf(id) }}</el-tag>
                <el-tag
                  v-if="hiddenAssignIds.length"
                  size="mini"
                  type="info"
                  class="pc-assign-more-tag"
                  @click.stop="openAssignTeachersDialog"
                >+{{ hiddenAssignIds.length }}</el-tag>
              </div>
              <el-select
                v-model="assignIds"
                multiple
                filterable
                collapse-tags
                size="mini"
                :placeholder="teacherOptions.length ? '添加教师' : '暂无组员'"
                class="pc-assign-select"
                :loading="teacherLoading"
                :disabled="!teacherOptions.length"
              >
                <el-option
                  v-for="t in teacherOptions"
                  :key="t.Id"
                  :label="t.TeacherName"
                  :value="t.Id"
                />
              </el-select>
            </div>
            <el-button type="primary" size="mini" :loading="assignSaving" @click="saveAssign">保存</el-button>
          </div>
          <p v-if="!assignIds.length" class="pc-assign-tip">
            <i class="el-icon-warning-outline"></i>
            <span>未安排教师时，H5 无人可上传该单元</span>
          </p>

          <div class="pc-lessons">
            <div class="pc-lessons__bar">
              <div class="pc-seg" role="tablist">
                <button
                  type="button"
                  role="tab"
                  class="pc-seg__btn"
                  :class="{ 'is-active': lessonTab === '1' }"
                  @click="lessonTab = '1'"
                >
                  新授课 {{ lessonCountOf(activeUnit, 1) }}/20
                </button>
                <button
                  type="button"
                  role="tab"
                  class="pc-seg__btn"
                  :class="{ 'is-active': lessonTab === '2' }"
                  @click="lessonTab = '2'"
                >
                  练习课 {{ lessonCountOf(activeUnit, 2) }}/20
                </button>
              </div>
              <div class="pc-lessons__ops">
                <el-button type="text" size="mini" icon="el-icon-download" title="下载导入模板（单元+课时）" @click="downloadTemplate">模板</el-button>
                <el-button type="text" size="mini" icon="el-icon-upload2" title="Excel 导入单元与课时" :loading="importing" @click="triggerImport">导入</el-button>
                <el-button type="text" size="mini" icon="el-icon-s-grid" @click="openBatchLessonDialog">批量生成</el-button>
                <el-button type="text" size="mini" icon="el-icon-plus" @click="openLessonDialog()">新增</el-button>
              </div>
            </div>
            <div class="pc-table-wrap">
              <el-table
                ref="lessonTable"
                :data="pagedLessons"
                height="100%"
                size="mini"
                class="pc-table"
                empty-text="暂无课时，可新增或使用「导入」"
              >
                <el-table-column prop="LessonNo" label="序号" width="64" />
                <el-table-column prop="LessonName" label="课时名称" min-width="140" />
                <el-table-column label="操作" width="200" align="center">
                  <template slot-scope="scope">
                    <div class="table-op-pair">
                      <el-button type="text" size="small" class="table-op-link" @click="openLessonDialog(scope.row)">编辑</el-button>
                      <el-button type="text" size="small" class="table-op-link table-op-link--danger" @click="removeLesson(scope.row)">删除</el-button>
                    </div>
                  </template>
                </el-table-column>
              </el-table>
            </div>
            <div v-if="filteredLessons.length > 0" class="pc-lesson-pager">
              <el-pagination
                layout="total, sizes, prev, pager, next, jumper"
                :current-page.sync="lessonPage"
                :page-sizes="[5, 10, 20]"
                :page-size="lessonPageSize"
                :total="filteredLessons.length"
                @size-change="onLessonPageSizeChange"
              />
            </div>
          </div>
        </template>
        <div v-else class="pc-empty">
          <i class="el-icon-document"></i>
          <p>{{ activeTextbookId ? "请选择单元，或点击「新增」" : "请先选择课本册" }}</p>
        </div>
      </section>
    </div>

    <el-dialog :title="bookDialogTitle" :visible.sync="bookDialogVisible" width="440px" append-to-body>
      <el-form :model="bookForm" label-width="90px" size="small">
        <el-form-item label="课本册名" required>
          <el-input v-model="bookForm.Title" placeholder="如：四年级数学下册" />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="bookForm.SortOrder" :min="0" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="bookDialogVisible=false">取消</el-button>
        <el-button type="primary" :loading="bookSaving" @click="submitBook">确定</el-button>
      </div>
    </el-dialog>

    <el-dialog :title="unitDialogTitle" :visible.sync="unitDialogVisible" width="480px" append-to-body>
      <el-form :model="unitForm" label-width="90px" size="small">
        <el-form-item label="单元名称" required>
          <el-input v-model="unitForm.UnitName" />
        </el-form-item>
        <el-form-item label="页码">
          <el-input-number v-model="unitForm.PageNo" :min="0" />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="unitForm.SortOrder" :min="0" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="unitDialogVisible=false">取消</el-button>
        <el-button type="primary" :loading="unitSaving" @click="submitUnit">确定</el-button>
      </div>
    </el-dialog>

    <el-dialog :title="lessonDialogTitle" :visible.sync="lessonDialogVisible" width="480px" append-to-body>
      <el-form :model="lessonForm" label-width="90px" size="small">
        <el-form-item label="类型">
          <el-radio-group v-model="lessonForm.LessonType">
            <el-radio :label="1">新授课</el-radio>
            <el-radio :label="2">练习课</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="序号" required>
          <el-input-number v-model="lessonForm.LessonNo" :min="1" :max="20" />
        </el-form-item>
        <el-form-item label="名称">
          <el-input v-model="lessonForm.LessonName" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="lessonDialogVisible=false">取消</el-button>
        <el-button type="primary" :loading="lessonSaving" @click="submitLesson">确定</el-button>
      </div>
    </el-dialog>

    <el-dialog title="批量生成课时" :visible.sync="batchLessonVisible" width="440px" append-to-body>
      <el-form label-width="90px" size="small">
        <el-form-item label="当前单元">
          <span>{{ (activeUnit && activeUnit.UnitName) || "—" }}</span>
        </el-form-item>
        <el-form-item label="类型">
          <el-radio-group v-model="batchLessonForm.LessonType">
            <el-radio :label="1">新授课</el-radio>
            <el-radio :label="2">练习课</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="生成数量" required>
          <el-input-number v-model="batchLessonForm.Count" :min="1" :max="batchLessonRemain" />
          <span class="pc-batch-hint">还可生成 {{ batchLessonRemain }} 个（上限 20）</span>
        </el-form-item>
        <el-form-item label="命名规则">
          <span class="pc-batch-hint">{{ batchLessonNamePreview }}</span>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="batchLessonVisible=false">取消</el-button>
        <el-button type="primary" :loading="batchLessonSaving" :disabled="batchLessonRemain <= 0" @click="submitBatchLessons">生成</el-button>
      </div>
    </el-dialog>

    <!-- 安排教师完整名单（超出 +N 点击打开） -->
    <el-dialog
      :visible.sync="assignTeachersVisible"
      width="480px"
      append-to-body
      title="安排教师"
    >
      <p class="pc-assign-dialog__sub">
        {{ (activeUnit && activeUnit.UnitName) || "当前单元" }} · 共 {{ assignIds.length }} 人
      </p>
      <div v-if="assignIds.length" class="pc-assign-dialog__list">
        <el-tag
          v-for="id in assignIds"
          :key="'all-' + id"
          size="small"
          closable
          disable-transitions
          class="pc-assign-dialog__tag"
          @close="removeAssignId(id)"
        >{{ teacherNameOf(id) }}</el-tag>
      </div>
      <div v-else class="pc-empty pc-empty--sm"><p>暂无安排教师</p></div>
      <div slot="footer">
        <el-button size="small" @click="assignTeachersVisible = false">关闭</el-button>
        <el-button type="primary" size="small" :loading="assignSaving" @click="saveAssignFromDialog">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getTextbooks,
  saveTextbook,
  deleteTextbook,
  getCatalogTree,
  saveUnit,
  deleteUnit,
  saveLesson,
  batchGenerateLessons,
  deleteLesson,
  saveUnitAssignments,
  downloadCatalogTemplate,
  importCatalogExcel,
  getGroupTeachers
} from "@/api/modules/teaching-research";

/**
 * 课本册 + 单元目录维护面板（嵌在教研组弹窗中使用）。样式更新：2026-07-26
 */
export default {
  name: "PrepCatalog",
  props: {
    groupId: { type: [Number, String], required: true },
    groupName: { type: String, default: "" },
    schoolId: { type: [Number, String], default: 0 },
    groupTeacherIds: { type: Array, default: () => [] }
  },
  data() {
    return {
      loading: false,
      importing: false,
      textbooks: [],
      activeTextbookId: 0,
      units: [],
      activeUnitId: 0,
      bookKeyword: "",
      unitKeyword: "",
      assignIds: [],
      assignSaving: false,
      teacherOptions: [],
      teacherLoading: false,
      assignChipLimit: 99,
      assignTeachersVisible: false,
      lessonTab: "1",
      lessonPage: 1,
      lessonPageSize: 5,
      bookDialogVisible: false,
      bookDialogTitle: "新增课本册",
      bookSaving: false,
      bookForm: { Id: 0, Title: "", SortOrder: 0 },
      unitDialogVisible: false,
      unitDialogTitle: "新增单元",
      unitSaving: false,
      unitForm: { Id: 0, UnitName: "", PageNo: null, SortOrder: 0 },
      lessonDialogVisible: false,
      lessonDialogTitle: "新增课时",
      lessonSaving: false,
      lessonForm: { Id: 0, LessonType: 1, LessonNo: 1, LessonName: "" },
      batchLessonVisible: false,
      batchLessonSaving: false,
      batchLessonForm: { LessonType: 1, Count: 5 }
    };
  },
  computed: {
    activeBook() {
      return this.textbooks.find(b => Number(b.Id) === Number(this.activeTextbookId)) || null;
    },
    activeUnit() {
      return this.units.find(u => Number(u.Id) === Number(this.activeUnitId)) || null;
    },
    /** 课本册搜索过滤 */
    filteredTextbooks() {
      const kw = (this.bookKeyword || "").trim().toLowerCase();
      if (!kw) return this.textbooks;
      return this.textbooks.filter(b => String(b.Title || "").toLowerCase().includes(kw));
    },
    /** 单元搜索过滤 */
    filteredUnits() {
      const kw = (this.unitKeyword || "").trim().toLowerCase();
      if (!kw) return this.units;
      return this.units.filter(u => String(u.UnitName || "").toLowerCase().includes(kw));
    },
    filteredLessons() {
      if (!this.activeUnit) return [];
      const type = Number(this.lessonTab);
      return (this.activeUnit.Lessons || []).filter(l => Number(l.LessonType) === type);
    },
    /** 当前页课时（每页 5 条） */
    pagedLessons() {
      const list = this.filteredLessons;
      const size = this.lessonPageSize || 5;
      const page = Math.max(1, this.lessonPage || 1);
      const start = (page - 1) * size;
      return list.slice(start, start + size);
    },
    visibleAssignIds() {
      return (this.assignIds || []).slice(0, this.assignChipLimit);
    },
    hiddenAssignIds() {
      return (this.assignIds || []).slice(this.assignChipLimit);
    },
    /** 当前批量生成类型剩余可生成数 */
    batchLessonRemain() {
      if (!this.activeUnit) return 0;
      const type = Number(this.batchLessonForm.LessonType) || 1;
      const used = this.lessonCountOf(this.activeUnit, type);
      return Math.max(0, 20 - used);
    },
    /** 批量生成命名预览 */
    batchLessonNamePreview() {
      const unitName = (this.activeUnit && this.activeUnit.UnitName) || "单元名";
      const type = Number(this.batchLessonForm.LessonType) === 2 ? "练习课" : "授课";
      return `${unitName}${type}1、${unitName}${type}2…`;
    }
  },
  watch: {
    groupId: {
      immediate: true,
      handler(val) {
        if (val) {
          this.activeTextbookId = 0;
          this.activeUnitId = 0;
          this.textbooks = [];
          this.units = [];
          this.teacherOptions = [];
          this.bookKeyword = "";
          this.unitKeyword = "";
          this.reloadAll();
        }
      }
    },
    assignIds() {
      this.recalcAssignChipLimit();
      this.$nextTick(() => this.relayoutLessonTable());
    },
    teacherOptions() {
      this.recalcAssignChipLimit();
    },
    lessonTab() {
      this.lessonPage = 1;
      this.$nextTick(() => this.relayoutLessonTable());
    },
    activeUnitId() {
      this.lessonPage = 1;
      this.$nextTick(() => this.relayoutLessonTable());
    },
    activeTextbookId() {
      this.unitKeyword = "";
    },
    filteredLessons(list) {
      const size = this.lessonPageSize || 5;
      const maxPage = Math.max(1, Math.ceil((list || []).length / size) || 1);
      if (this.lessonPage > maxPage) this.lessonPage = maxPage;
      this.$nextTick(() => this.relayoutLessonTable());
    },
    "batchLessonForm.LessonType"() {
      const remain = this.batchLessonRemain;
      if (this.batchLessonForm.Count > remain) {
        this.batchLessonForm.Count = Math.max(1, remain);
      }
    }
  },
  methods: {
    async reloadAll() {
      await this.loadTextbooks();
    },
    async loadTextbooks() {
      if (!this.groupId) return;
      this.loading = true;
      try {
        const res = await getTextbooks({ groupId: this.groupId });
        if (!res.success) {
          this.$message.error(res.msg || "加载课本册失败");
          return;
        }
        const list = res.response || [];
        this.textbooks = list.map(b => ({
          Id: Number(b.Id != null ? b.Id : b.id) || 0,
          GroupId: Number(b.GroupId != null ? b.GroupId : b.groupId) || 0,
          Title: b.Title || b.title || "",
          SortOrder: b.SortOrder != null ? b.SortOrder : (b.sortOrder || 0),
          UnitCount: b.UnitCount != null ? b.UnitCount : (b.unitCount || 0)
        }));
        if (!this.activeTextbookId && this.textbooks.length) {
          this.activeTextbookId = this.textbooks[0].Id;
        } else if (this.activeTextbookId && !this.textbooks.some(b => Number(b.Id) === Number(this.activeTextbookId))) {
          this.activeTextbookId = this.textbooks.length ? this.textbooks[0].Id : 0;
        }
        if (this.activeTextbookId) {
          await this.loadTree();
        } else {
          this.units = [];
          this.activeUnitId = 0;
        }
      } finally {
        this.loading = false;
      }
    },
    onSelectBook(id) {
      this.activeTextbookId = Number(id) || 0;
      this.activeUnitId = 0;
      this.loadTree();
    },
    /** 统计单元下某类型课时数 */
    lessonCountOf(unit, lessonType) {
      if (!unit || !unit.Lessons) return 0;
      return unit.Lessons.filter(l => Number(l.LessonType) === Number(lessonType)).length;
    },
    /** 单元是否已安排备课教师 */
    isUnitAssigned(unit) {
      return !!(unit && unit.TeacherMasterIds && unit.TeacherMasterIds.length);
    },
    /** 同步选中单元：有单元时默认选中第一个，避免右侧空白 */
    syncActiveUnit() {
      if (!this.units.length) {
        this.activeUnitId = 0;
        this.assignIds = [];
        return;
      }
      const exists = this.units.some(u => Number(u.Id) === Number(this.activeUnitId));
      const targetId = exists ? this.activeUnitId : this.units[0].Id;
      this.onSelectUnit(targetId);
    },
    openBookDialog(row) {
      if (row) {
        this.bookDialogTitle = "编辑课本册";
        this.bookForm = { Id: row.Id, Title: row.Title, SortOrder: row.SortOrder || 0 };
      } else {
        this.bookDialogTitle = "新增课本册";
        this.bookForm = { Id: 0, Title: "", SortOrder: this.textbooks.length + 1 };
      }
      this.bookDialogVisible = true;
    },
    async submitBook() {
      if (!this.bookForm.Title || !this.bookForm.Title.trim()) {
        this.$message.error("请输入课本册名");
        return;
      }
      this.bookSaving = true;
      try {
        const res = await saveTextbook({
          Id: this.bookForm.Id,
          GroupId: this.groupId,
          Title: this.bookForm.Title,
          SortOrder: this.bookForm.SortOrder,
          Status: 0
        });
        if (res.success) {
          this.$message.success(res.msg || "保存成功");
          this.bookDialogVisible = false;
          if (res.response) this.activeTextbookId = res.response;
          await this.loadTextbooks();
        } else this.$message.error(res.msg || "保存失败");
      } finally {
        this.bookSaving = false;
      }
    },
    removeBook() {
      if (!this.activeBook) return;
      this.$confirm("确认删除该课本册？有单元时无法删除。", "提示", { type: "warning" }).then(async () => {
        const res = await deleteTextbook({ textbookId: this.activeBook.Id });
        if (res.success) {
          this.$message.success("删除成功");
          this.activeTextbookId = 0;
          this.loadTextbooks();
        } else this.$message.error(res.msg || "删除失败");
      }).catch(() => {});
    },
    /** 下载目录导入模板（列：单元名称/页码/课时类型/课时序号/课时名称） */
    downloadTemplate() {
      downloadCatalogTemplate().then(res => {
        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "集体备课目录导入模板.xlsx";
        a.click();
        window.URL.revokeObjectURL(url);
        this.$message.info("模板含单元与课时列；类型填「新授课」或「练习课」，序号 1~20");
      });
    },
    /** 导入当前课本册的单元与课时 */
    triggerImport() {
      if (!this.activeTextbookId) {
        this.$message.error("请先选择课本册");
        return;
      }
      this.$refs.importFile && this.$refs.importFile.click();
    },
    async onImportFile(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file || !this.activeTextbookId) return;
      this.importing = true;
      try {
        const res = await importCatalogExcel(this.activeTextbookId, file);
        if (res.success) {
          const count = (res.response && res.response.SuccessCount) || 0;
          this.$message.success(res.msg || (count ? `成功导入 ${count} 条课时行` : "导入成功"));
          await this.loadTextbooks();
        } else {
          const errors = (res.response && res.response.Errors) || [];
          this.$message({
            type: "error",
            duration: 8000,
            message: errors.length ? errors.slice(0, 5).join("；") : (res.msg || "导入失败")
          });
        }
      } finally {
        this.importing = false;
      }
    },
    async loadTree() {
      if (!this.activeTextbookId) return;
      this.loading = true;
      try {
        const res = await getCatalogTree({ textbookId: this.activeTextbookId });
        if (!res.success) {
          this.$message.error(res.msg || "加载目录失败");
          return;
        }
        const tree = res.response || {};
        this.units = (tree.Units || tree.units || []).map(u => ({
          Id: Number(u.Id != null ? u.Id : u.id) || 0,
          UnitName: u.UnitName || u.unitName,
          PageNo: u.PageNo != null ? u.PageNo : u.pageNo,
          SortOrder: u.SortOrder != null ? u.SortOrder : u.sortOrder,
          TeacherMasterIds: (u.TeacherMasterIds || u.teacherMasterIds || []).map(Number),
          TeacherNames: u.TeacherNames || u.teacherNames || [],
          Lessons: (u.Lessons || u.lessons || []).map(l => ({
            Id: Number(l.Id != null ? l.Id : l.id) || 0,
            UnitId: Number(l.UnitId != null ? l.UnitId : l.unitId) || 0,
            LessonType: Number(l.LessonType != null ? l.LessonType : l.lessonType) || 0,
            LessonNo: Number(l.LessonNo != null ? l.LessonNo : l.lessonNo) || 0,
            LessonName: l.LessonName || l.lessonName || ""
          }))
        }));
        this.syncActiveUnit();
        await this.ensureTeachers();
      } finally {
        this.loading = false;
      }
    },
    /** 加载教研组教师选项（用组员接口，保证显示姓名而非 ID） */
    async ensureTeachers() {
      const gid = Number(this.groupId) || 0;
      if (!gid) {
        this.teacherOptions = [];
        return;
      }
      this.teacherLoading = true;
      try {
        const res = await getGroupTeachers({ groupId: gid });
        const list = (res && res.response) || [];
        this.teacherOptions = list.map(t => ({
          Id: Number(t.TeacherMasterId != null ? t.TeacherMasterId : t.teacherMasterId) || 0,
          TeacherName: t.TeacherName || t.teacherName || ""
        })).filter(t => t.Id > 0);

        // 兜底：把单元已安排但选项里缺失的姓名补进 options，避免显示裸 ID
        this.units.forEach(u => {
          const ids = u.TeacherMasterIds || [];
          const names = u.TeacherNames || [];
          ids.forEach((id, i) => {
            if (!this.teacherOptions.some(o => Number(o.Id) === Number(id))) {
              this.teacherOptions.push({
                Id: Number(id),
                TeacherName: names[i] || String(id)
              });
            }
          });
        });
      } finally {
        this.teacherLoading = false;
        this.recalcAssignChipLimit();
      }
    },
    /** 根据姓名估算宽度，一行尽量多展示，超出折叠为 +N */
    recalcAssignChipLimit() {
      this.$nextTick(() => {
        const row = this.$refs.assignChipsRow;
        if (!row || !(this.assignIds || []).length) {
          this.assignChipLimit = (this.assignIds || []).length || 0;
          return;
        }
        const maxW = Math.max(row.clientWidth - 52, 80);
        let used = 0;
        let n = 0;
        for (const id of this.assignIds) {
          const name = this.teacherNameOf(id);
          const est = Math.min(Math.max(name.length * 13 + 36, 56), 140);
          if (n > 0 && used + est > maxW) break;
          used += est + 6;
          n += 1;
        }
        this.assignChipLimit = Math.max(1, n);
      });
    },
    teacherNameOf(id) {
      const t = (this.teacherOptions || []).find(x => Number(x.Id) === Number(id));
      if (t && t.TeacherName) return t.TeacherName;
      const u = this.activeUnit;
      if (u && u.TeacherMasterIds && u.TeacherNames) {
        const idx = u.TeacherMasterIds.findIndex(x => Number(x) === Number(id));
        if (idx >= 0 && u.TeacherNames[idx]) return u.TeacherNames[idx];
      }
      return String(id);
    },
    removeAssignId(id) {
      this.assignIds = (this.assignIds || []).filter(x => Number(x) !== Number(id));
      if (!(this.assignIds || []).length) {
        this.assignTeachersVisible = false;
      }
      this.recalcAssignChipLimit();
    },
    /** 超出折叠的教师：弹窗看全员名单 */
    openAssignTeachersDialog() {
      if (!(this.assignIds || []).length) return;
      this.assignTeachersVisible = true;
    },
    onSelectUnit(id) {
      this.activeUnitId = Number(id) || 0;
      const u = this.activeUnit;
      const allow = new Set((this.groupTeacherIds || []).map(Number).filter(x => x > 0));
      const ids = u ? [...(u.TeacherMasterIds || [])].map(Number) : [];
      // 剔除已离组但仍残留的安排 ID
      this.assignIds = allow.size ? ids.filter(x => allow.has(x)) : ids;
      this.recalcAssignChipLimit();
    },
    openUnitDialog(row) {
      if (!this.activeTextbookId) {
        this.$message.error("请先选择课本册");
        return;
      }
      if (row) {
        this.unitDialogTitle = "编辑单元";
        this.unitForm = {
          Id: row.Id,
          UnitName: row.UnitName,
          PageNo: row.PageNo,
          SortOrder: row.SortOrder || 0
        };
      } else {
        this.unitDialogTitle = "新增单元";
        this.unitForm = { Id: 0, UnitName: "", PageNo: null, SortOrder: this.units.length + 1 };
      }
      this.unitDialogVisible = true;
    },
    async submitUnit() {
      if (!this.unitForm.UnitName || !this.unitForm.UnitName.trim()) {
        this.$message.error("请输入单元名称");
        return;
      }
      this.unitSaving = true;
      try {
        const res = await saveUnit({
          Id: this.unitForm.Id,
          TextbookId: this.activeTextbookId,
          UnitName: this.unitForm.UnitName,
          PageNo: this.unitForm.PageNo,
          SortOrder: this.unitForm.SortOrder
        });
        if (res.success) {
          this.$message.success(res.msg || "保存成功");
          this.unitDialogVisible = false;
          if (res.response) this.activeUnitId = Number(res.response) || 0;
          await this.loadTextbooks();
        } else this.$message.error(res.msg || "保存失败");
      } finally {
        this.unitSaving = false;
      }
    },
    removeUnit(row) {
      // 勿直接绑 @click="removeUnit"，否则会把 MouseEvent 当成 row
      const target = row && row.Id ? row : this.activeUnit;
      if (!target || !target.Id) return;
      const name = target.UnitName || target.unitName || "该单元";
      this.$confirm(`确认删除单元「${name}」及其课时？`, "提示", { type: "warning" }).then(async () => {
        const res = await deleteUnit({ unitId: target.Id });
        if (res.success) {
          this.$message.success("删除成功");
          if (Number(this.activeUnitId) === Number(target.Id)) this.activeUnitId = 0;
          this.loadTextbooks();
        } else this.$message.error(res.msg || "删除失败");
      }).catch(() => {});
    },
    async saveAssign() {
      if (!this.activeUnit) return false;
      const doSave = async () => {
        this.assignSaving = true;
        try {
          const res = await saveUnitAssignments({
            UnitId: this.activeUnit.Id,
            TeacherMasterIds: this.assignIds
          });
          if (res.success) {
            if (!this.assignIds.length) {
              this.$message.warning(res.msg || "已清空安排；未安排时 H5 无人可上传该单元");
            } else {
              this.$message.success(res.msg || "安排已保存");
            }
            await this.loadTree();
            return true;
          }
          this.$message.error(res.msg || "保存失败");
          return false;
        } finally {
          this.assignSaving = false;
        }
      };
      if (!this.assignIds.length) {
        try {
          await this.$confirm("未安排教师时，H5 无人可上传该单元。确认清空安排？", "提示", {
            type: "warning",
            confirmButtonText: "确认清空",
            cancelButtonText: "取消"
          });
          return await doSave();
        } catch (e) {
          return false;
        }
      }
      return await doSave();
    },
    /** 弹窗内保存安排并关闭 */
    async saveAssignFromDialog() {
      const ok = await this.saveAssign();
      if (ok) this.assignTeachersVisible = false;
    },
    openLessonDialog(row) {
      if (!this.activeUnit) {
        this.$message.error("请先选择单元");
        return;
      }
      if (row) {
        this.lessonDialogTitle = "编辑课时";
        this.lessonForm = {
          Id: row.Id,
          LessonType: row.LessonType,
          LessonNo: row.LessonNo,
          LessonName: row.LessonName || ""
        };
      } else {
        this.lessonDialogTitle = "新增课时";
        const type = Number(this.lessonTab);
        if (this.filteredLessons.length >= 20) {
          this.$message.error(type === 1 ? "新授课最多 20 个课时" : "练习课最多 20 个课时");
          return;
        }
        const used = this.filteredLessons.map(l => l.LessonNo);
        let next = 1;
        while (used.includes(next) && next <= 20) next++;
        this.lessonForm = { Id: 0, LessonType: type, LessonNo: next, LessonName: "" };
      }
      this.lessonDialogVisible = true;
    },
    async submitLesson() {
      if (!this.activeUnit) return;
      this.lessonSaving = true;
      try {
        const res = await saveLesson({
          Id: this.lessonForm.Id,
          UnitId: this.activeUnit.Id,
          LessonType: this.lessonForm.LessonType,
          LessonNo: this.lessonForm.LessonNo,
          LessonName: this.lessonForm.LessonName,
          SortOrder: this.lessonForm.LessonNo
        });
        if (res.success) {
          this.$message.success(res.msg || "保存成功");
          this.lessonDialogVisible = false;
          this.lessonTab = String(this.lessonForm.LessonType);
          await this.loadTree();
        } else this.$message.error(res.msg || "保存失败");
      } finally {
        this.lessonSaving = false;
      }
    },
    /** 打开批量生成课时弹窗 */
    openBatchLessonDialog() {
      if (!this.activeUnit) {
        this.$message.error("请先选择单元");
        return;
      }
      const type = Number(this.lessonTab) || 1;
      const remain = Math.max(0, 20 - this.lessonCountOf(this.activeUnit, type));
      if (remain <= 0) {
        this.$message.error(type === 1 ? "新授课已满 20 个" : "练习课已满 20 个");
        return;
      }
      this.batchLessonForm = {
        LessonType: type,
        Count: Math.min(5, remain)
      };
      this.batchLessonVisible = true;
    },
    /** 提交批量生成课时 */
    async submitBatchLessons() {
      if (!this.activeUnit) return;
      const count = Number(this.batchLessonForm.Count) || 0;
      if (count < 1) {
        this.$message.error("请输入生成数量");
        return;
      }
      if (count > this.batchLessonRemain) {
        this.$message.error(`最多还能生成 ${this.batchLessonRemain} 个`);
        return;
      }
      this.batchLessonSaving = true;
      try {
        const res = await batchGenerateLessons({
          UnitId: this.activeUnit.Id,
          LessonType: this.batchLessonForm.LessonType,
          Count: count
        });
        if (res.success) {
          this.$message.success(res.msg || "生成成功");
          this.batchLessonVisible = false;
          this.lessonTab = String(this.batchLessonForm.LessonType);
          await this.loadTree();
        } else this.$message.error(res.msg || "生成失败");
      } finally {
        this.batchLessonSaving = false;
      }
    },
    /** 课时分页条数变更 */
    onLessonPageSizeChange(size) {
      this.lessonPageSize = size || 5;
      this.lessonPage = 1;
      this.$nextTick(() => this.relayoutLessonTable());
    },
    /** 重新计算课时表高度（表体内滚动） */
    relayoutLessonTable() {
      const table = this.$refs.lessonTable;
      if (table && typeof table.doLayout === "function") table.doLayout();
    },
    removeLesson(row) {
      this.$confirm("确认删除该课时？", "提示", { type: "warning" }).then(async () => {
        const res = await deleteLesson({ lessonId: row.Id });
        if (res.success) {
          this.$message.success("删除成功");
          this.loadTree();
        } else this.$message.error(res.msg || "删除失败");
      }).catch(() => {});
    }
  }
};
</script>

<style scoped>
/* 数据密集体验：统一壳层 + 左侧列表轨 + 右侧工作区。更新于 2026-07-26 */
.pc-panel {
  --pc-primary: #1e3a8a;
  --pc-accent: #3b82f6;
  --pc-bg: #f8fafc;
  --pc-rail: #f1f5f9;
  --pc-line: #e2e8f0;
  --pc-muted: #64748b;
  --pc-text: #0f172a;
  height: auto;
  min-height: 0;
  overflow: hidden;
  color: var(--pc-text);
}
.pc-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}
.pc-path {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
  font-size: 13px;
  line-height: 1.4;
}
.pc-path__item {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--pc-muted);
  font-weight: 500;
}
.pc-path__item.is-on {
  color: var(--pc-primary);
  font-weight: 700;
}
.pc-path__item.is-dim {
  color: #94a3b8;
  font-weight: 500;
}
.pc-path__sep {
  font-size: 12px;
  color: #cbd5e1;
}
.pc-toolbar__actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}
.is-danger {
  color: #dc2626 !important;
}

.pc-shell {
  display: grid;
  grid-template-columns: 196px 248px minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
  /* 固定高度：再抬高一点，右侧课时区更舒展 */
  height: 600px;
  min-height: 600px;
  max-height: 600px;
  border: 1px solid var(--pc-line);
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.pc-pane {
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  max-height: 100%;
  height: 100%;
  overflow: hidden;
  background: #fff;
}
.pc-pane--books,
.pc-pane--units {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border-right: 1px solid var(--pc-line);
}
.pc-pane__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  height: 40px;
  padding: 0 10px 0 12px;
  border-bottom: 1px solid rgba(226, 232, 240, 0.9);
  background: rgba(255, 255, 255, 0.88);
  flex-shrink: 0;
}
.pc-pane__head-left {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.pc-pane__title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #475569;
}
.pc-pane__count {
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  border-radius: 999px;
  background: #e2e8f0;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
}
.pc-pane__search {
  flex-shrink: 0;
  padding: 8px 10px 6px;
}
.pc-pane__search >>> .el-input__inner {
  height: 30px;
  line-height: 30px;
  border-radius: 8px;
  border-color: #e2e8f0;
  background: #fff;
  box-shadow: 0 1px 1px rgba(15, 23, 42, 0.02);
}
.pc-pane__search >>> .el-input__inner:focus {
  border-color: #93c5fd;
}
.pc-pane__foot {
  margin-top: auto;
  padding: 6px 10px;
  border-top: 1px solid rgba(226, 232, 240, 0.9);
  background: rgba(255, 255, 255, 0.88);
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}
.pc-foot-hint {
  margin-right: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
}
.pc-lessons__ops {
  display: flex;
  align-items: center;
  gap: 0;
  flex-shrink: 0;
}
.pc-lessons__ops .el-button {
  padding: 0 4px;
}
.pc-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}
.pc-list::-webkit-scrollbar {
  width: 5px;
}
.pc-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 999px;
}
.pc-list::-webkit-scrollbar-track {
  background: transparent;
}
.pc-row {
  display: block;
  width: 100%;
  border: 1px solid transparent;
  border-left: 3px solid transparent;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.55);
  padding: 9px 10px;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}
.pc-row:hover {
  background: #fff;
  border-color: #e2e8f0;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.pc-row.is-active {
  background: #fff;
  border-color: #bfdbfe;
  border-left-color: var(--pc-accent);
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
}
.pc-row:focus-visible {
  outline: 2px solid var(--pc-accent);
  outline-offset: 1px;
}
.pc-row__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.pc-row__main {
  display: block;
  min-width: 0;
  font-size: 13px;
  font-weight: 650;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pc-row.is-active .pc-row__main {
  color: var(--pc-primary);
}
.pc-row__tags {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.pc-row__page {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
}
.pc-badge-warn {
  font-size: 10px;
  font-weight: 700;
  line-height: 1.4;
  padding: 1px 6px;
  border-radius: 999px;
  color: #c2410c;
  background: #fff7ed;
  border: 1px solid #fed7aa;
}
.pc-assign-tip {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 7px 14px;
  font-size: 12px;
  color: #9a3412;
  background: #fff7ed;
  border-bottom: 1px solid #ffedd5;
  flex-shrink: 0;
}
.pc-assign-tip i {
  font-size: 14px;
  color: #ea580c;
}
.pc-batch-hint {
  margin-left: 8px;
  font-size: 12px;
  color: var(--pc-muted);
  line-height: 1.5;
}
.pc-lesson-pager {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
}
.pc-row__aside {
  display: block;
  margin-top: 3px;
  font-size: 11px;
  color: #94a3b8;
}
.pc-row__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}
.pc-chip {
  display: inline-block;
  padding: 1px 7px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.55;
  font-variant-numeric: tabular-nums;
}
.pc-row.is-active .pc-chip {
  background: #eff6ff;
  color: #2563eb;
}
.pc-file {
  display: none;
}

.pc-pane--detail {
  background: #fff;
  overflow: hidden;
}
.pc-detail-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px 12px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}
.pc-detail-head__text {
  min-width: 0;
}
.pc-detail-head h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 750;
  color: var(--pc-text);
  letter-spacing: -0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pc-detail-head p {
  margin: 5px 0 0;
  font-size: 12px;
  color: var(--pc-muted);
  line-height: 1.45;
}
.pc-dot {
  margin: 0 4px;
  color: #cbd5e1;
}
.pc-detail-head__ops {
  flex-shrink: 0;
  display: flex;
  gap: 2px;
  padding-top: 1px;
}
.pc-assign {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}
.pc-assign__label {
  flex-shrink: 0;
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
}
.pc-assign-body {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.pc-assign-chips {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 4px;
  overflow: hidden;
}
.pc-assign-chips .el-tag {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
}
.pc-assign-more-tag {
  cursor: pointer;
  flex-shrink: 0;
  background: #dbeafe !important;
  border-color: #93c5fd !important;
  color: #1e3a8a !important;
  font-weight: 700;
}
.pc-assign-more-tag:hover {
  background: #bfdbfe !important;
}
.pc-assign-dialog__sub {
  margin: 0 0 12px;
  font-size: 13px;
  color: #64748b;
}
.pc-assign-dialog__list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 360px;
  overflow-y: auto;
}
.pc-assign-dialog__tag {
  margin: 0;
}
.pc-assign-select {
  width: 148px;
  flex-shrink: 0;
}
/* 左侧已用芯片展示姓名，下拉内隐藏重复标签 */
.pc-assign-select >>> .el-select__tags {
  display: none !important;
}
.pc-assign-select >>> .el-input__inner {
  height: 28px !important;
}
.pc-lessons {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 10px 14px 12px;
  overflow: hidden;
}
.pc-lessons__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
  flex-shrink: 0;
}
.pc-seg {
  display: inline-flex;
  padding: 2px;
  border-radius: 8px;
  background: #f1f5f9;
}
.pc-seg__btn {
  border: none;
  background: transparent;
  padding: 5px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--pc-muted);
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
}
.pc-seg__btn:hover {
  color: var(--pc-primary);
}
.pc-seg__btn.is-active {
  background: #fff;
  color: var(--pc-primary);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
}
.pc-seg__btn:focus-visible {
  outline: 2px solid var(--pc-accent);
  outline-offset: 1px;
}
.pc-table-wrap {
  flex: 1 1 auto;
  min-height: 0;
  height: 0;
  overflow: hidden;
}
.pc-table {
  width: 100%;
}
.pc-table >>> .el-table__header th {
  background: #f8fafc !important;
  color: #475569;
  font-weight: 700;
  font-size: 12px;
}
.pc-table >>> .el-table__body td {
  font-size: 13px;
}
/* 课时表滚动条对齐单元列表细条样式 */
.pc-table >>> .el-table__body-wrapper {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}
.pc-table >>> .el-table__body-wrapper::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.pc-table >>> .el-table__body-wrapper::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 999px;
}
.pc-table >>> .el-table__body-wrapper::-webkit-scrollbar-track {
  background: transparent;
}
.pc-table >>> .el-table__body-wrapper::-webkit-scrollbar-button {
  display: none;
  width: 0;
  height: 0;
}

.pc-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: var(--pc-muted);
  font-size: 13px;
  padding: 28px 14px;
  text-align: center;
}
.pc-empty i {
  font-size: 26px;
  color: #93c5fd;
}
.pc-empty--sm {
  padding: 24px 10px;
}
.pc-empty p {
  margin: 0;
}

@media (max-width: 1100px) {
  .pc-shell {
    grid-template-columns: 1fr;
    height: 760px;
    min-height: 760px;
    max-height: 760px;
  }
  .pc-pane--books,
  .pc-pane--units {
    border-right: none;
    border-bottom: 1px solid var(--pc-line);
    min-height: 0;
    max-height: 220px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .pc-row,
  .pc-seg__btn {
    transition: none;
  }
}
</style>
